import type { PassoDoFormulario } from "../commons/JornadaDoFormulario";
import { CAMPOS_DA_PESSOA, MedidasParaSeguro } from "../crm/camposDoLead";
import {
  CargoInput,
  ContactAddressInputs,
  ContactChannelsInputs,
  ContactDocumentInputs,
  EmpresaInput,
  EstadoCivilInput,
  GeneroInput,
  RendaMensalInput,
} from "./ContactInputs";
import type { Identifier } from "ra-core";
import { useWatch } from "react-hook-form";
import { ResponsavelInput } from "../crm/responsavel";
import { passosDaAdn } from "../crm/adn/ResumoDaAdn";

/**
 * A ficha de contato como jornada: os mesmos campos de `ContactInputs`, poucos
 * por passo. Usada pelo cadastro de contato e pelo novo lead (que acrescenta o
 * passo do negócio na frente) e pela edição (`ContactEdit.tsx`), que abre os
 * mesmos passos como atalhos, com o Salvar em todos.
 *
 * Só os dois primeiros passos têm algo obrigatório (o nome e, desde a
 * migration 240, o celular); o resto é o que surgiu na conversa, e dá para
 * avançar em branco.
 */
/**
 * "o cliente" / "a cliente" pelo gênero marcado no primeiro passo; contato
 * antigo, sem gênero gravado, fica com "o(a) cliente".
 */
const useArtigo = () => {
  const genero = useWatch({ name: "details.gender" });
  return genero === "female" ? "a" : genero === "male" ? "o" : "o(a)";
};
const OCliente = () => <>{useArtigo()} cliente</>;
const DoCliente = () => <>d{useArtigo()} cliente</>;

export const passosDaFicha = ({
  requireDocumentAndAddress = false,
  exigirTelefone = false,
  responsavel,
  comAdn = false,
}: {
  requireDocumentAndAddress?: boolean;
  /** É lead: só muda o texto de apoio — o celular é obrigatório em todo contato. */
  exigirTelefone?: boolean;
  /**
   * Mostra o campo "Responsável" (crm/responsavel.tsx) com este dono atual.
   * Só onde a troca faz sentido: nos cadastros que abrem a partir de uma venda
   * ou cotação, o contato criado é escolhido na hora por quem está vendendo.
   */
  responsavel?: { donoAtual: Identifier | null | undefined };
  /** Seguro de vida: a ADN (`crm/adn/`) entra antes das observações. */
  comAdn?: boolean;
}): PassoDoFormulario[] => [
  {
    id: "pessoa",
    rotulo: "Pessoa",
    titulo: "Dados pessoais",
    apoio: "Nome obrigatório. CPF opcional, validado quando informado.",
    campos: ["details.person_type", "details.cpf", "details.name", "details.birth_date"],
    conteudo: (
      <>
        <ContactDocumentInputs requireDocumentAndAddress={requireDocumentAndAddress} />
        <GeneroInput />
      </>
    ),
  },
  {
    id: "contato",
    rotulo: "Contato",
    titulo: "Dados de contato",
    apoio: exigirTelefone
      ? "Celular obrigatório; e-mail opcional."
      : "Celular obrigatório e exclusivo deste cadastro; e-mail opcional.",
    campos: ["details.email", "details.phone"],
    conteudo: <ContactChannelsInputs />,
  },
  {
    id: "endereco",
    rotulo: "Endereço",
    titulo: "Endereço residencial ou comercial",
    apoio: "Rua, bairro e cidade preenchidos a partir do CEP.",
    campos: [
      "details.zipcode",
      "details.address",
      "details.address_number",
      "details.neighborhood",
      "details.state",
      "details.city",
    ],
    conteudo: (
      <ContactAddressInputs requireDocumentAndAddress={requireDocumentAndAddress} comTitulo={false} />
    ),
  },
  {
    id: "trabalho",
    rotulo: "Trabalho",
    titulo: <>Profissão <DoCliente /></>,
    campos: ["details.monthly_income"],
    conteudo: (
      <>
        {CAMPOS_DA_PESSOA.profissao}
        <CargoInput />
        <EmpresaInput />
        <RendaMensalInput obrigatoria={requireDocumentAndAddress} />
      </>
    ),
  },
  {
    id: "familia",
    rotulo: "Família",
    titulo: "Composição familiar",
    apoio: "Estado civil, cônjuge e filhos.",
    conteudo: (
      <>
        <EstadoCivilInput />
        {CAMPOS_DA_PESSOA.nomesDaFamilia}
        {CAMPOS_DA_PESSOA.nascimentosDaFamilia}
        {CAMPOS_DA_PESSOA.rendaDoConjuge}
      </>
    ),
  },
  {
    id: "bens",
    rotulo: "Bens",
    titulo: "Patrimônio e compromissos financeiros",
    apoio: "Investimentos e bens, financiamentos e empréstimos em andamento.",
    conteudo: (
      <>
        {CAMPOS_DA_PESSOA.investimentos}
        {CAMPOS_DA_PESSOA.patrimonio}
        {CAMPOS_DA_PESSOA.financiamentos}
        {CAMPOS_DA_PESSOA.emprestimos}
      </>
    ),
  },
  {
    id: "seguros",
    rotulo: "Seguros",
    titulo: <>Seguros que <OCliente /> possui</>,
    conteudo: (
      <>
        {CAMPOS_DA_PESSOA.seguroDeVida}
        {CAMPOS_DA_PESSOA.seguroAuto}
        {CAMPOS_DA_PESSOA.planoDeSaude}
        <MedidasParaSeguro />
      </>
    ),
  },
  ...(comAdn ? passosDaAdn() : []),
  {
    id: "notas",
    rotulo: "Notas",
    titulo: "Observações",
    conteudo: (
      <>
        {CAMPOS_DA_PESSOA.briefing}
        {CAMPOS_DA_PESSOA.outrasInformacoes}
        {responsavel && <ResponsavelInput donoAtual={responsavel.donoAtual} />}
      </>
    ),
  },
];
